import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import StatsCard from './components/StatsCard';
import BookFormModal from './components/BookFormModal';
import BulkUploadModal from './components/BulkUploadModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'business', label: 'Business' },
  { value: 'leadership', label: 'Leadership' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'sales', label: 'Sales' },
  { value: 'finance', label: 'Finance' },
  { value: 'strategy', label: 'Strategy' },
  { value: 'management', label: 'Management' },
  { value: 'productivity', label: 'Productivity' },
  { value: 'entrepreneurship', label: 'Entrepreneurship' },
  { value: 'personal-development', label: 'Personal Development' },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showBookForm, setShowBookForm] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [deletingBook, setDeletingBook] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, categories: 0, newBooks: 0, bestsellers: 0 });

  const checkAdminAccess = useCallback(async () => {
    const { data: { session } } = await supabase?.auth?.getSession();
    if (!session?.user) { navigate('/admin-login'); return false; }
    const meta = session?.user?.user_metadata;
    const appMeta = session?.user?.app_metadata;
    if (meta?.role !== 'admin' && appMeta?.role !== 'admin') {
      await supabase?.auth?.signOut();
      navigate('/admin-login');
      return false;
    }
    return true;
  }, [navigate]);

  const fetchBooks = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase?.from('ebooks')?.select('*')?.order('created_at', { ascending: false });
      if (selectedCategory !== 'all') query = query?.eq('category', selectedCategory);
      if (searchQuery?.trim()) query = query?.ilike('title', `%${searchQuery?.trim()}%`);
      const { data, error } = await query;
      if (error) throw error;
      setBooks(data || []);
    } catch (err) {
      console.error('Failed to fetch books:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const { data, error } = await supabase?.from('ebooks')?.select('category, is_new, is_bestseller');
      if (error) throw error;
      const cats = new Set(data?.map((b) => b.category));
      setStats({
        total: data?.length || 0,
        categories: cats?.size,
        newBooks: data?.filter((b) => b?.is_new)?.length || 0,
        bestsellers: data?.filter((b) => b?.is_bestseller)?.length || 0,
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err?.message);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const isAdmin = await checkAdminAccess();
      if (isAdmin) { fetchBooks(); fetchStats(); }
    };
    init();
  }, [checkAdminAccess, fetchBooks, fetchStats]);

  const handleSignOut = async () => {
    await supabase?.auth?.signOut();
    navigate('/admin-login');
  };

  const handleBookSaved = (savedBook) => {
    setBooks((prev) => {
      const exists = prev?.find((b) => b?.id === savedBook?.id);
      if (exists) return prev?.map((b) => b?.id === savedBook?.id ? savedBook : b);
      return [savedBook, ...prev];
    });
    fetchStats();
    setShowBookForm(false);
    setEditingBook(null);
  };

  const handleBookDeleted = (bookId) => {
    setBooks((prev) => prev?.filter((b) => b?.id !== bookId));
    fetchStats();
    setDeletingBook(null);
  };

  const handleBulkUploaded = () => {
    fetchBooks();
    fetchStats();
  };

  const categoryCount = (cat) => {
    if (cat === 'all') return books?.length;
    return books?.filter((b) => b?.category === cat)?.length;
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border flex flex-col transition-transform duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 lg:static lg:flex`}>
        {/* Logo */}
        <div className="p-5 border-b border-border">
          <img
            src="/assets/images/Logo_final_no_background-1771860991795-1772040132991.png"
            alt="StrategyShelf Logo"
            className="h-8 w-auto object-contain"
          />
          <p className="text-xs text-muted-foreground mt-1 font-medium">Admin Dashboard</p>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1">
          <div className="px-3 py-2 rounded-lg bg-primary/10">
            <div className="flex items-center gap-2.5">
              <Icon name="BookOpen" size={16} color="var(--color-primary)" />
              <span className="text-sm font-medium text-primary">Book Catalog</span>
            </div>
          </div>
        </nav>

        {/* Category filter in sidebar */}
        <div className="p-4 border-t border-border">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Filter by Category</p>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {CATEGORIES?.map((cat) => (
              <button
                key={cat?.value}
                onClick={() => { setSelectedCategory(cat?.value); setSidebarOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  selectedCategory === cat?.value
                    ? 'bg-primary/10 text-primary font-medium' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <span>{cat?.label}</span>
                <span className="text-xs">{categoryCount(cat?.value)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Sign out */}
        <div className="p-4 border-t border-border">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="LogOut" size={16} />
            Sign Out
          </button>
          <button
            onClick={() => navigate('/e-book-catalog')}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors mt-1"
          >
            <Icon name="ExternalLink" size={16} />
            View Store
          </button>
        </div>
      </aside>
      {/* Sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-card border-b border-border px-4 md:px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="Menu" size={20} />
          </button>
          <h1 className="text-lg font-heading font-semibold text-foreground">Book Catalog Management</h1>
          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={() => setShowBulkUpload(true)}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              <Icon name="Upload" size={16} />
              <span className="hidden sm:inline">Bulk Upload</span>
            </button>
            <button
              onClick={() => { setEditingBook(null); setShowBookForm(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Icon name="Plus" size={16} />
              <span className="hidden sm:inline">Add Book</span>
            </button>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard title="Total Books" value={stats?.total} icon="BookOpen" color="primary" loading={statsLoading} />
            <StatsCard title="Categories" value={stats?.categories} icon="Tag" color="blue" loading={statsLoading} />
            <StatsCard title="New Titles" value={stats?.newBooks} icon="Sparkles" color="green" loading={statsLoading} />
            <StatsCard title="Bestsellers" value={stats?.bestsellers} icon="TrendingUp" color="orange" loading={statsLoading} />
          </div>

          {/* Search */}
          <div className="relative">
            <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder="Search books by title..."
              className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>

          {/* Books table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h2 className="text-sm font-medium text-foreground">
                {selectedCategory === 'all' ? 'All Books' : CATEGORIES?.find((c) => c?.value === selectedCategory)?.label}
                <span className="ml-2 text-muted-foreground">({books?.length})</span>
              </h2>
            </div>

            {loading ? (
              <div className="p-12 text-center">
                <Icon name="Loader2" size={32} color="var(--color-muted-foreground)" className="animate-spin mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Loading books...</p>
              </div>
            ) : books?.length === 0 ? (
              <div className="p-12 text-center">
                <Icon name="BookOpen" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-sm text-muted-foreground mb-4">No books found</p>
                <button
                  onClick={() => { setEditingBook(null); setShowBookForm(true); }}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  Add First Book
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Book</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">Category</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider hidden sm:table-cell">Price</th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Badges</th>
                      <th className="text-right px-5 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {books?.map((book) => (
                      <tr key={book?.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-14 rounded overflow-hidden flex-shrink-0 bg-muted">
                              <img
                                src={book?.cover_image || '/assets/images/no_image.png'}
                                alt={book?.cover_image_alt || book?.title + ' cover'}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.src = '/assets/images/no_image.png'; }}
                              />
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground line-clamp-2">{book?.title}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{book?.author}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 hidden md:table-cell">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground capitalize">
                            {book?.category}
                          </span>
                        </td>
                        <td className="px-4 py-4 hidden sm:table-cell">
                          <span className="text-sm font-medium text-foreground">${parseFloat(book?.price)?.toFixed(2)}</span>
                        </td>
                        <td className="px-4 py-4 hidden lg:table-cell">
                          <div className="flex gap-1.5">
                            {book?.is_new && (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded font-medium">New</span>
                            )}
                            {book?.is_bestseller && (
                              <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded font-medium">Bestseller</span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => { setEditingBook(book); setShowBookForm(true); }}
                              className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                              title="Edit book"
                            >
                              <Icon name="Pencil" size={15} />
                            </button>
                            <button
                              onClick={() => setDeletingBook(book)}
                              className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove book"
                            >
                              <Icon name="Trash2" size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
      {/* Modals */}
      {showBookForm && (
        <BookFormModal
          book={editingBook}
          onClose={() => { setShowBookForm(false); setEditingBook(null); }}
          onSaved={handleBookSaved}
        />
      )}
      {showBulkUpload && (
        <BulkUploadModal
          onClose={() => setShowBulkUpload(false)}
          onUploaded={handleBulkUploaded}
        />
      )}
      {deletingBook && (
        <DeleteConfirmModal
          book={deletingBook}
          onClose={() => setDeletingBook(null)}
          onDeleted={handleBookDeleted}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
